import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";
import { presetCollectionContract } from "../presets/collection-contracts.js";
import {
  exactFields,
  frozenClone,
  isRecord,
  payloadsEqual,
  savedPresetName,
  timestampRange,
} from "../presets/preset-internals.js";

const SCHEMA_VERSION = 1;
const ACCOUNT_PATH = "/api/v1/account/saved-presets";
const MAX_PRESETS = 256;
const MAX_IMPORT_PRESETS = 256;
const MAX_COLLECTION_PAYLOAD_BYTES = 4 * 1024 * 1024;
const RESPONSE_MAX_BYTES = 5 * 1024 * 1024;
const PRESET_ID_PATTERN = /^preset_[0-9a-f]{32}$/;
const DECIMAL_REVISION_PATTERN = /^[1-9][0-9]*$/;
const TEXT_ENCODER = new TextEncoder();

const PAYLOAD_KINDS = Object.freeze({
  "calculator-presets": "calculatorPreset",
  "calculator-layouts": "calculatorLayout",
  "calculator-overlays-v4": "calculatorOverlayV4",
  "map-presets": "mapPreset",
  "fishydex-presets": "fishydexPreset",
});

function invalid(message) {
  throw new TypeError(message);
}

function isAmbiguousFetchFailure(error) {
  return error instanceof TypeError;
}

function collectionKey(value) {
  if (typeof value !== "string" || !Object.hasOwn(PAYLOAD_KINDS, value)) {
    invalid("Account saved-preset collection is unsupported.");
  }
  return value;
}

function presetId(value, label = "Account saved-preset ID") {
  if (typeof value !== "string" || !PRESET_ID_PATTERN.test(value)) {
    invalid(`${label} is invalid.`);
  }
  return value;
}

function importedSourceId(value) {
  if (
    typeof value !== "string"
    || value.length === 0
    || value.length > 160
    || value.trim() !== value
  ) {
    invalid("Imported saved-preset source ID is invalid.");
  }
  return value;
}

function positiveRevision(value, label) {
  if (!Number.isSafeInteger(value) || value <= 0) {
    invalid(`${label} is invalid.`);
  }
  return value;
}

function revisionNumber(value, label) {
  if (typeof value !== "string" || !DECIMAL_REVISION_PATTERN.test(value)) {
    invalid(`${label} is invalid.`);
  }
  const revision = Number(value);
  if (!Number.isSafeInteger(revision)) invalid(`${label} is invalid.`);
  return revision;
}

function optionalCollectionRevision(options) {
  if (!Object.hasOwn(options, "expectedRevision")) return undefined;
  if (options.expectedRevision === null) return null;
  return revisionNumber(
    options.expectedRevision,
    "Expected account saved-preset collection revision",
  );
}

function requiredPresetRevision(options) {
  if (!Object.hasOwn(options, "expectedPresetRevision")) {
    invalid("Expected account saved-preset item revision is required.");
  }
  return revisionNumber(
    options.expectedPresetRevision,
    "Expected account saved-preset item revision",
  );
}

function collectionPrecondition(options) {
  if (!Object.hasOwn(options, "expectedRevision")) {
    invalid("Expected account saved-preset collection state is required.");
  }
  if (options.expectedRevision === null) {
    return Object.freeze({ kind: "missing" });
  }
  return Object.freeze({
    kind: "ready",
    revision: revisionNumber(
      options.expectedRevision,
      "Expected account saved-preset collection revision",
    ),
  });
}

function exactOptions(value, allowed, label) {
  if (value === undefined) return {};
  if (!isRecord(value) || Object.keys(value).some((field) => !allowed.includes(field))) {
    invalid(`${label} contains unsupported fields.`);
  }
  return value;
}

function payloadKind(key) {
  return PAYLOAD_KINDS[key];
}

function decodePayloadValue(key, value) {
  const decoded = presetCollectionContract(key).decodePayload(value);
  if (!isRecord(decoded)) invalid("Account saved-preset payload is invalid.");
  return decoded;
}

function commandPayload(key, value) {
  return Object.freeze({
    kind: payloadKind(key),
    value: decodePayloadValue(key, value),
  });
}

function encodedPayloadBytes(value) {
  return TEXT_ENCODER.encode(JSON.stringify(value)).byteLength;
}

function decodeWirePayload(key, value) {
  if (
    !exactFields(value, ["kind", "value"])
    || value.kind !== payloadKind(key)
  ) {
    invalid("Account saved-preset payload wrapper is invalid.");
  }
  const decoded = decodePayloadValue(key, value.value);
  return {
    decoded,
    encodedBytes: encodedPayloadBytes({
      kind: value.kind,
      value: decoded,
    }),
  };
}

function decodeItem(value, key) {
  if (!exactFields(value, [
    "id",
    "revision",
    "payloadRevision",
    "name",
    "payload",
    "createdAt",
    "updatedAt",
  ])) {
    invalid("Account saved-preset item is invalid.");
  }
  const revision = positiveRevision(value.revision, "Account saved-preset item revision");
  const payloadRevision = positiveRevision(
    value.payloadRevision,
    "Account saved-preset payload revision",
  );
  if (payloadRevision > revision) {
    invalid("Account saved-preset payload revision is invalid.");
  }
  const timestamps = timestampRange(
    value.createdAt,
    value.updatedAt,
    "Account saved preset",
  );
  const payload = decodeWirePayload(key, value.payload);
  return {
    item: {
      id: presetId(value.id),
      revision: String(revision),
      name: savedPresetName(value.name, "Account saved-preset name"),
      payloadRevisionId: String(payloadRevision),
      payload: payload.decoded,
      tombstone: false,
      ...timestamps,
    },
    payloadBytes: payload.encodedBytes,
  };
}

function decodeProjection(value, expectedKey) {
  if (
    exactFields(value, ["kind", "schemaVersion", "collection"])
    && value.kind === "missing"
    && value.schemaVersion === SCHEMA_VERSION
    && value.collection === expectedKey
  ) {
    return frozenClone({ kind: "missing", collectionKey: expectedKey });
  }
  if (
    !exactFields(value, [
      "kind",
      "schemaVersion",
      "collection",
      "revision",
      "presets",
    ])
    || value.kind !== "ready"
    || value.schemaVersion !== SCHEMA_VERSION
    || value.collection !== expectedKey
    || !Array.isArray(value.presets)
    || value.presets.length > MAX_PRESETS
  ) {
    invalid("Account saved-preset collection projection is invalid.");
  }
  const revision = positiveRevision(
    value.revision,
    "Account saved-preset collection revision",
  );
  const seen = new Set();
  const presets = [];
  let payloadBytes = 0;
  for (const rawItem of value.presets) {
    const decoded = decodeItem(rawItem, expectedKey);
    if (Number(decoded.item.revision) > revision) {
      invalid("Account saved-preset item revision exceeds its collection revision.");
    }
    if (seen.has(decoded.item.id)) {
      invalid("Account saved-preset IDs must be unique.");
    }
    seen.add(decoded.item.id);
    payloadBytes += decoded.payloadBytes;
    if (payloadBytes > MAX_COLLECTION_PAYLOAD_BYTES) {
      invalid("Account saved-preset collection payloads exceed their byte limit.");
    }
    presets.push(decoded.item);
  }
  return frozenClone({
    kind: "ready",
    collectionKey: expectedKey,
    revision: String(revision),
    presets,
  });
}

function decodeAcknowledgement(value, operationId, key) {
  if (
    !exactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    invalid("Account saved-preset acknowledgement is invalid.");
  }
  return Object.freeze({
    kind: value.kind,
    authoritativeState: decodeProjection(value.authoritativeState, key),
  });
}

function accountUrl(apiBaseUrl, key) {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    invalid("Account saved-preset API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    invalid("Account saved-preset API base URL must be an HTTP origin.");
  }
  return `${base.origin}${ACCOUNT_PATH}/${key}`;
}

function defaultCreatePresetId(cryptoRef = globalThis.crypto) {
  if (typeof cryptoRef?.randomUUID === "function") {
    return `preset_${cryptoRef.randomUUID().replaceAll("-", "").toLowerCase()}`;
  }
  if (typeof cryptoRef?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return `preset_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")).join("")}`;
  }
  invalid("Secure account saved-preset identity generation is unavailable.");
}

function assertReadySession(session) {
  // This validates both ready state and its current mutation authorization,
  // without putting account identity or authorization into a request body.
  accountMutationHeaders(session);
}

function findPreset(state, id) {
  return state.kind === "ready"
    ? state.presets.find((preset) => preset.id === id) ?? null
    : null;
}

function presetPayloadsEqual(key, left, right) {
  return payloadsEqual(presetCollectionContract(key), left, right);
}

function supersededResult(state, context) {
  return frozenClone({
    ...conflictResult(state, context),
    reason: "saved-operation-superseded",
  });
}

function appliedResult(method, state, context) {
  if (method === "reset") {
    const validMissingReset = context.expectedRevision === null
      && state.kind === "missing";
    const validReadyReset = context.expectedRevision !== null
      && state.kind === "ready"
      && state.presets.length === 0;
    if (!validMissingReset && !validReadyReset) {
      return supersededResult(state, context);
    }
    return state;
  }
  if (method === "remove") {
    if (state.kind === "ready" && findPreset(state, context.presetId)) {
      return supersededResult(state, context);
    }
    return frozenClone({
      ...state,
      effect: { kind: "preset-deleted", presetId: context.presetId },
    });
  }
  if (method === "importMany") {
    const presets = context.presets.map((expected) => findPreset(state, expected.id));
    const postconditionHolds = state.kind === "ready"
      && presets.every((preset, index) => {
        const expected = context.presets[index];
        return preset !== null
          && preset.name === expected.name
          && preset.createdAt === expected.createdAt
          && preset.updatedAt === expected.updatedAt
          && presetPayloadsEqual(context.collectionKey, preset.payload, expected.payload);
      });
    if (!postconditionHolds) {
      return supersededResult(state, context);
    }
    return frozenClone({
      ...state,
      effect: {
        kind: "presets-imported",
        presets,
        presetIds: context.presets.map((preset) => preset.id),
      },
    });
  }
  const preset = findPreset(state, context.presetId);
  const postconditionHolds = preset !== null
    && (method !== "create" || (
      preset.name === context.name
      && presetPayloadsEqual(context.collectionKey, preset.payload, context.payload)
    ))
    && (method !== "update"
      || presetPayloadsEqual(context.collectionKey, preset.payload, context.payload))
    && (method !== "rename" || preset.name === context.name);
  if (!postconditionHolds) {
    return supersededResult(state, context);
  }
  return frozenClone({
    ...state,
    effect: {
      kind: method === "create" ? "preset-created" : "preset-updated",
      preset,
    },
  });
}

function conflictResult(state, context) {
  const actualPreset = context.presetId
    ? findPreset(state, context.presetId)
    : null;
  return frozenClone({
    kind: "conflict",
    collectionKey: context.collectionKey,
    expectedRevision: context.expectedRevision ?? null,
    actualRevision: state.kind === "ready" ? state.revision : null,
    ...(context.expectedPresetRevision === undefined ? {} : {
      expectedPresetRevision: context.expectedPresetRevision,
      actualPresetRevision: actualPreset?.revision ?? null,
    }),
    authoritative: state,
  });
}

export function createSavedPresetAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
  createPresetId = defaultCreatePresetId,
} = {}) {
  if (
    typeof fetchImpl !== "function"
    || typeof createOperationId !== "function"
    || typeof createPresetId !== "function"
  ) {
    invalid("Account saved-preset provider requires fetch and identity factories.");
  }

  function operationId() {
    const value = createOperationId();
    if (!isAccountOperationId(value)) {
      invalid("Account saved-preset operation ID is invalid.");
    }
    return value;
  }

  function nextPresetId(occupied = new Set()) {
    for (let attempt = 0; attempt < 16; attempt += 1) {
      const value = presetId(createPresetId());
      if (!occupied.has(value)) {
        occupied.add(value);
        return value;
      }
    }
    invalid("Account saved-preset identity generation repeatedly collided.");
  }

  async function load(session, rawKey, { signal } = {}) {
    assertReadySession(session);
    const key = collectionKey(rawKey);
    const response = await fetchImpl(accountUrl(apiBaseUrl, key), {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (response.status !== 200) throw new Error("Account saved-preset request failed.");
    return decodeProjection(await readBoundedAccountJson(
      response,
      "Account saved-preset collection",
      RESPONSE_MAX_BYTES,
    ), key);
  }

  async function post(session, key, command, context, signal) {
    const url = accountUrl(apiBaseUrl, key);
    const headers = accountMutationHeaders(session);
    const body = JSON.stringify(command);
    let response;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        response = await fetchImpl(url, {
          method: "POST",
          cache: "no-store",
          credentials: "include",
          headers,
          redirect: "error",
          signal,
          body,
        });
        break;
      } catch (error) {
        if (
          attempt > 0
          || signal?.aborted === true
          || error?.name === "AbortError"
          || !isAmbiguousFetchFailure(error)
        ) {
          throw error;
        }
      }
    }
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Account saved-preset mutation failed.");
    }
    const acknowledgement = decodeAcknowledgement(
      await readBoundedAccountJson(
        response,
        "Account saved-preset acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      command.operationId,
      key,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      invalid("Account saved-preset status and acknowledgement disagree.");
    }
    return acknowledgement.kind === "applied"
      ? appliedResult(context.method, acknowledgement.authoritativeState, context)
      : conflictResult(acknowledgement.authoritativeState, context);
  }

  async function create(session, rawKey, rawOptions) {
    const options = exactOptions(
      rawOptions,
      ["expectedRevision", "id", "name", "payload", "signal"],
      "Account saved-preset create options",
    );
    const key = collectionKey(rawKey);
    const expectedRevision = optionalCollectionRevision(options);
    const createdId = options.id === undefined
      ? nextPresetId()
      : presetId(options.id);
    const name = savedPresetName(options.name, "Account saved-preset name");
    const payload = commandPayload(key, options.payload);
    const operation = operationId();
    return post(session, key, {
      kind: "create",
      schemaVersion: SCHEMA_VERSION,
      operationId: operation,
      collection: key,
      presetId: createdId,
      name,
      payload,
    }, {
      method: "create",
      collectionKey: key,
      expectedRevision: expectedRevision === null ? null : expectedRevision?.toString(),
      presetId: createdId,
      name,
      payload: payload.value,
    }, options.signal);
  }

  async function update(session, rawKey, rawPresetId, rawOptions) {
    const options = exactOptions(
      rawOptions,
      ["expectedRevision", "expectedPresetRevision", "payload", "signal"],
      "Account saved-preset update options",
    );
    const key = collectionKey(rawKey);
    const id = presetId(rawPresetId);
    const expectedRevision = optionalCollectionRevision(options);
    const expectedPresetRevision = requiredPresetRevision(options);
    const payload = commandPayload(key, options.payload);
    const operation = operationId();
    return post(session, key, {
      kind: "updatePayload",
      schemaVersion: SCHEMA_VERSION,
      operationId: operation,
      collection: key,
      presetId: id,
      expectedPresetRevision,
      payload,
    }, {
      method: "update",
      collectionKey: key,
      expectedRevision: expectedRevision === null ? null : expectedRevision?.toString(),
      expectedPresetRevision: String(expectedPresetRevision),
      presetId: id,
      payload: payload.value,
    }, options.signal);
  }

  async function rename(session, rawKey, rawPresetId, rawOptions) {
    const options = exactOptions(
      rawOptions,
      ["expectedRevision", "expectedPresetRevision", "name", "signal"],
      "Account saved-preset rename options",
    );
    const key = collectionKey(rawKey);
    const id = presetId(rawPresetId);
    const expectedRevision = optionalCollectionRevision(options);
    const expectedPresetRevision = requiredPresetRevision(options);
    const name = savedPresetName(options.name, "Account saved-preset name");
    const operation = operationId();
    return post(session, key, {
      kind: "rename",
      schemaVersion: SCHEMA_VERSION,
      operationId: operation,
      collection: key,
      presetId: id,
      expectedPresetRevision,
      name,
    }, {
      method: "rename",
      collectionKey: key,
      expectedRevision: expectedRevision === null ? null : expectedRevision?.toString(),
      expectedPresetRevision: String(expectedPresetRevision),
      presetId: id,
      name,
    }, options.signal);
  }

  async function remove(session, rawKey, rawPresetId, rawOptions) {
    const options = exactOptions(
      rawOptions,
      ["expectedRevision", "expectedPresetRevision", "signal"],
      "Account saved-preset delete options",
    );
    const key = collectionKey(rawKey);
    const id = presetId(rawPresetId);
    const expectedRevision = optionalCollectionRevision(options);
    const expectedPresetRevision = requiredPresetRevision(options);
    const operation = operationId();
    return post(session, key, {
      kind: "delete",
      schemaVersion: SCHEMA_VERSION,
      operationId: operation,
      collection: key,
      presetId: id,
      expectedPresetRevision,
    }, {
      method: "remove",
      collectionKey: key,
      expectedRevision: expectedRevision === null ? null : expectedRevision?.toString(),
      expectedPresetRevision: String(expectedPresetRevision),
      presetId: id,
    }, options.signal);
  }

  async function importMany(session, rawKey, entries, rawOptions) {
    const options = exactOptions(
      rawOptions,
      ["expectedRevision", "signal"],
      "Account saved-preset import options",
    );
    const key = collectionKey(rawKey);
    if (!Array.isArray(entries) || entries.length > MAX_IMPORT_PRESETS) {
      invalid("Account saved-preset import is invalid.");
    }
    const expectedState = collectionPrecondition(options);
    const occupied = new Set();
    let payloadBytes = 0;
    const remapped = entries.map((entry) => {
      if (!exactFields(entry, ["id", "name", "payload", "createdAt", "updatedAt"])) {
        invalid("Imported saved preset is invalid.");
      }
      importedSourceId(entry.id);
      const timestamps = timestampRange(
        entry.createdAt,
        entry.updatedAt,
        "Imported saved preset",
      );
      const payload = commandPayload(key, entry.payload);
      payloadBytes += encodedPayloadBytes(payload);
      if (payloadBytes > MAX_COLLECTION_PAYLOAD_BYTES) {
        invalid("Account saved-preset import payloads exceed their byte limit.");
      }
      return {
        id: nextPresetId(occupied),
        name: savedPresetName(entry.name, "Imported saved-preset name"),
        payload,
        ...timestamps,
      };
    });
    const operation = operationId();
    const expectedRevision = expectedState.kind === "ready"
      ? String(expectedState.revision)
      : null;
    return post(session, key, {
      kind: "importMany",
      schemaVersion: SCHEMA_VERSION,
      operationId: operation,
      collection: key,
      expectedState,
      presets: remapped,
    }, {
      method: "importMany",
      collectionKey: key,
      expectedRevision,
      presets: remapped.map((entry) => ({
        id: entry.id,
        name: entry.name,
        payload: entry.payload.value,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
      })),
    }, options.signal);
  }

  async function reset(session, rawKey, rawOptions) {
    const options = exactOptions(
      rawOptions,
      ["expectedRevision", "signal"],
      "Account saved-preset reset options",
    );
    const key = collectionKey(rawKey);
    const expectedState = collectionPrecondition(options);
    const operation = operationId();
    return post(session, key, {
      kind: "reset",
      schemaVersion: SCHEMA_VERSION,
      operationId: operation,
      collection: key,
      expectedState,
    }, {
      method: "reset",
      collectionKey: key,
      expectedRevision: expectedState.kind === "ready"
        ? String(expectedState.revision)
        : null,
    }, options.signal);
  }

  return Object.freeze({ load, create, update, rename, remove, importMany, reset });
}
